import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, RotateCcw, Info, Image as ImageIcon, Palette } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';
import { scoreLabel } from '@/lib/optimizationScoring';
import { getSearchVolumeInfo } from '@/lib/optimizeVariants';

const COMPONENT_LABELS = {
  completeness: 'Completeness', titleQuality: 'Title Quality', descriptionQuality: 'Description Quality',
  keywordUsage: 'Keyword Usage', hashtagUsage: 'Hashtag Usage', platformFields: 'Platform Fields', thumbnailReadiness: 'Thumbnail Readiness',
};
const COMPONENT_MAX = { completeness: 20, titleQuality: 15, descriptionQuality: 15, keywordUsage: 15, hashtagUsage: 15, platformFields: 10, thumbnailReadiness: 10 };

/**
 * Optimize → Review: the consolidated approval surface (section 14).
 * Every field shown here is still editable from the center workspace or
 * the other sidebar categories — this panel is a recap + the approval
 * action, not a separate data source. Approval is per-platform-variant
 * and maps onto the EXACT `status` value Scheduler.jsx already reads
 * (`'ready'` only once approved) — see `statusForReview()`.
 */
export function OptimizeReviewPanel({ contentRef, variant, score, resolvedThumbnail, brandKit, onApprove, onReject, onResetToDraft, disabled }) {
  if (disabled) return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="optimize-review-empty">Select content and a platform first.</p>;

  const allKeywords = [...variant.keywords.brand, ...variant.keywords.platform, ...variant.keywords.content];
  const uniqueKeywords = Array.from(new Set(allKeywords.map((k) => k.toLowerCase())));
  const { label, color } = scoreLabel(score.overall);

  return (
    <div className="space-y-4" data-testid="optimize-review-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Content</p>
        <p className="text-sm text-white">{contentRef.title} <span className="text-[#8A93A6]">· {platformLabel(variant.platforms[0])}</span></p>
      </div>

      <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="optimize-review-score">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#8A93A6]">Optimization Score</span>
          <Badge style={{ borderColor: color, color }} variant="outline">{score.overall}/100 · {label}</Badge>
        </div>
        <div className="space-y-1.5">
          {Object.entries(score.components).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2" data-testid={`optimize-review-component-${key}`}>
              <span className="text-[10px] text-[#8A93A6] w-28 shrink-0">{COMPONENT_LABELS[key]}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-[#3A7BFF]" style={{ width: `${(val / COMPONENT_MAX[key]) * 100}%` }} />
              </div>
              <span className="text-[10px] text-[#8A93A6] w-10 text-right">{val}/{COMPONENT_MAX[key]}</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#555E6E] mt-2 flex items-start gap-1"><Info className="h-2.5 w-2.5 mt-0.5 shrink-0" />A rules-based completeness/readiness check, not a prediction of rankings, reach, or engagement.</p>
      </div>

      {score.suggestions.length > 0 && (
        <div data-testid="optimize-review-suggestions">
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1">Suggested Improvements</p>
          <ul className="space-y-1">
            {score.suggestions.map((s, i) => <li key={i} className="text-xs text-[#C0C7D4] flex items-start gap-1.5"><span className="text-amber-400 mt-0.5">•</span>{s}</li>)}
          </ul>
        </div>
      )}

      <div data-testid="optimize-review-keywords">
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1">Keywords — Search Volume &amp; Competition</p>
        {uniqueKeywords.length === 0 ? (
          <p className="text-xs text-[#8A93A6]">No keywords added yet.</p>
        ) : (
          <div className="space-y-1">
            {uniqueKeywords.map((k) => {
              const info = getSearchVolumeInfo(k, variant.platforms[0]);
              return (
                <div key={k} className="flex items-center justify-between text-xs" data-testid={`optimize-review-keyword-volume-${k}`}>
                  <span className="text-[#C0C7D4] truncate">{k}</span>
                  <span className="text-[10px] text-[#555E6E] shrink-0 ml-2">{info.available ? `${info.searchVolume} vol · ${info.competition} comp.` : 'Data Unavailable'}</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[10px] text-[#555E6E] mt-1">No connected keyword-research source yet — shown honestly rather than guessed.</p>
      </div>

      <div className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="optimize-review-thumbnail">
        <div className="h-9 w-16 rounded bg-black/50 flex items-center justify-center overflow-hidden shrink-0">
          {resolvedThumbnail.url ? <img src={resolvedThumbnail.url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-3.5 w-3.5 text-[#555E6E]" />}
        </div>
        <span className="text-[11px] text-[#8A93A6]">{resolvedThumbnail.url ? 'Thumbnail linked' : 'No thumbnail linked'}</span>
      </div>

      <div className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="optimize-review-brandkit">
        <Palette className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
        <span className="text-[11px] text-[#8A93A6]">{brandKit ? `${brandKit.name} applied` : 'No Brand Kit applied'}</span>
      </div>

      <div className="space-y-1.5 pt-1">
        <div className="flex items-center gap-2">
          <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={variant.reviewStatus === 'approved'} onClick={onApprove} data-testid="optimize-review-approve-button">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approve
          </Button>
          <Button size="sm" variant="outline" className="flex-1 border-red-500/30 text-red-400" disabled={variant.reviewStatus === 'rejected'} onClick={onReject} data-testid="optimize-review-reject-button">
            <XCircle className="h-3.5 w-3.5 mr-1.5" />Reject
          </Button>
        </div>
        {variant.reviewStatus !== 'draft' && (
          <Button size="sm" variant="ghost" className="w-full text-xs text-[#8A93A6] hover:text-white" onClick={onResetToDraft} data-testid="optimize-review-reset-button">
            <RotateCcw className="h-3 w-3 mr-1.5" />Back to Draft
          </Button>
        )}
        <p className="text-[10px] text-[#555E6E]" data-testid="optimize-review-status-note">
          Status: <span className="text-[#C0C7D4] capitalize">{variant.reviewStatus}</span>{variant.reviewStatus === 'approved' && ' — ready to appear in Scheduler.'}
        </p>
      </div>
    </div>
  );
}
